import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Info, Lock, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog'
import { Input, PasswordInput } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { OtpInput } from '../components/OtpInput'
import { confirmLogin, login, loadCurrentUser, type AuthStep } from '../api/auth'
import { translateCognitoError } from '../lib/cognitoErrors'
import { loadLastUsername, saveLastUsername } from '../lib/usernameStorage'
import { useAuthStore } from '../authStore'

type Stage =
  | { kind: 'credentials' }
  | { kind: 'mfa-sms' }
  | { kind: 'mfa-email' }
  | { kind: 'mfa-totp' }
  | { kind: 'mfa-selection'; options: Array<'SMS' | 'EMAIL' | 'TOTP'> }

function stageFromStep(step: AuthStep): Stage | null {
  switch (step) {
    case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
      return { kind: 'mfa-sms' }
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { kind: 'mfa-email' }
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { kind: 'mfa-totp' }
    case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
      return { kind: 'mfa-selection', options: ['SMS', 'EMAIL', 'TOTP'] }
    default:
      return null
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const setUser = useAuthStore((s) => s.setUser)

  const [stage, setStage] = useState<Stage>({ kind: 'credentials' })
  const [username, setUsername] = useState(() => loadLastUsername())
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSuccess() {
    saveLastUsername(username.trim())
    const u = await loadCurrentUser()
    setUser(u ?? { username: username.trim() })
    navigate('/', { replace: true })
  }

  async function onCredentialsSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const result = await login({ username, password })
      if (result.isSignedIn) return handleSuccess()
      const next = stageFromStep(result.nextStep.signInStep)
      if (next) setStage(next)
      else if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setError("È necessario impostare una nuova password. Contatta l'assistenza per procedere.")
      } else setError(`Step non gestito: ${result.nextStep.signInStep}`)
    } catch (err) {
      setError(translateCognitoError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function onConfirmCode(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const result = await confirmLogin(code)
      if (result.isSignedIn) return handleSuccess()
      const next = stageFromStep(result.nextStep.signInStep)
      if (next) {
        setStage(next)
        setCode('')
      } else setError(`Step non gestito: ${result.nextStep.signInStep}`)
    } catch (err) {
      setError(translateCognitoError(err))
    } finally {
      setSubmitting(false)
    }
  }

  async function onPickMfaType(choice: 'SMS' | 'EMAIL' | 'TOTP') {
    setError(null)
    setSubmitting(true)
    try {
      const result = await confirmLogin(choice)
      const next = stageFromStep(result.nextStep.signInStep)
      if (next) setStage(next)
      else setError(`Step non gestito: ${result.nextStep.signInStep}`)
    } catch (err) {
      setError(translateCognitoError(err))
    } finally {
      setSubmitting(false)
    }
  }

  function backToCredentials() {
    setStage({ kind: 'credentials' })
    setCode('')
    setError(null)
  }

  return (
    <AuthLayout>
      {stage.kind === 'credentials' && (
        <div className="w-full">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">Accedi</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Inserisci nome utente e password
            </p>
          </div>

          <form className="flex flex-col gap-4" onSubmit={onCredentialsSubmit}>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="username">Nome utente</Label>
              <Input
                id="username"
                placeholder="Il tuo nome utente"
                autoComplete="username"
                required
                leadingIcon={<User className="h-4 w-4" />}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                placeholder="La tua password"
                autoComplete="current-password"
                required
                leadingIcon={<Lock className="h-4 w-4" />}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={submitting} className="mt-2">
              {submitting ? <Spinner className="text-secondary-foreground" /> : 'Accedi'}
            </Button>
            <div className="mt-2 flex flex-col items-center gap-2 text-sm">
              <Link
                to="/forgot-password"
                className="font-semibold text-secondary underline-offset-2 hover:underline"
              >
                Imposta la password
              </Link>
              <UsernameHelpDialog />
            </div>
          </form>
        </div>
      )}

      {(stage.kind === 'mfa-sms' || stage.kind === 'mfa-email' || stage.kind === 'mfa-totp') && (
        <div className="w-full">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">Verifica</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {stage.kind === 'mfa-sms' && 'Inserisci il codice ricevuto via SMS.'}
              {stage.kind === 'mfa-email' && 'Inserisci il codice ricevuto via email.'}
              {stage.kind === 'mfa-totp' && 'Inserisci il codice della tua app authenticator.'}
            </p>
          </div>

          <form className="flex flex-col items-center gap-4" onSubmit={onConfirmCode}>
            <OtpInput
              value={code}
              onChange={setCode}
              autoFocus
              disabled={submitting}
              hasError={Boolean(error)}
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="submit"
              disabled={submitting || code.length < 6}
              className="mt-2 w-full"
            >
              {submitting ? <Spinner className="text-secondary-foreground" /> : 'Conferma'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={backToCredentials}
              className="text-muted-foreground"
            >
              Torna al login
            </Button>
          </form>
        </div>
      )}

      {stage.kind === 'mfa-selection' && (
        <div className="w-full">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">Verifica</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Scegli come ricevere il codice di sicurezza.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {stage.options.map((opt) => (
              <Button
                key={opt}
                variant="secondary"
                disabled={submitting}
                onClick={() => onPickMfaType(opt)}
              >
                {opt === 'SMS' && 'Invia un SMS'}
                {opt === 'EMAIL' && "Invia un'email"}
                {opt === 'TOTP' && 'Usa app authenticator'}
              </Button>
            ))}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={backToCredentials}
              className="text-muted-foreground"
            >
              Torna al login
            </Button>
          </div>
        </div>
      )}
    </AuthLayout>
  )
}

/**
 * Info dialog su dove è stato inviato il nome utente al primo accesso.
 * Replica `dialog.alert({ title: 'Non hai il nome utente?', ...variant: 'info' })`
 * del mobile (src/screens/login/v2/SignInScreen.js).
 */
function UsernameHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Non hai il nome utente?
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-secondary/15 text-secondary">
            <Info className="h-5 w-5" />
          </div>
          <DialogTitle>Non hai il nome utente?</DialogTitle>
          <DialogDescription>
            Il nome utente ti è stato inviato via mail. Verifica la tua casella postale
            elettronica, anche nella cartella Spam. Se non lo trovi, scrivici e saremo lieti
            di aiutarti.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="default" className="w-full sm:w-auto">
              Ho capito
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
